import { Box, Checkbox } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { materialStateEnum, stepModeEnum } from '../../constants';
import { useCreateDailyToolMutation, useDailyToolMutation, useDeleteDailyToolMutation } from '../../hooks/mutations/useDailyToolMutation';
import { useDailyResource } from '../../hooks/useDailyResource';
import { DataItem } from '../../services/apiSLTP/dailyReports/activities';
import { DailyTool } from '../../services/apiSLTP/dailyReports/dailyTools';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import DraggableCell from '../dnd/DraggableCell';
import DroppableCell from '../dnd/DroppableCell';
import ManageDailyToolModal from '../modals/operations/planning/ManageDailyToolModal';
import IconButton from '../ui/IconButton';
import { DND_ITEM_TYPES } from '../../constants/dnd';

interface ToolsCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    hiddenPlusIcon?: boolean;
    refetch?: () => void;
    createTransfer?: boolean;
}

const ToolsCell: React.FC<ToolsCellProps> = ({
    activity,
    activityIndex,
    activities,
    disabled = false,
    hiddenPlusIcon = false,
    refetch = () => {},
    createTransfer = true,
}) => {
    const { t } = useTranslation();
    const itemType = DND_ITEM_TYPES.TOOLS;

    const { mutate: updateDailyTool } = useDailyToolMutation({ onSuccess: refetch });
    const { mutate: deleteDailyTool } = useDeleteDailyToolMutation({ onSuccess: refetch });
    const { mutate: createDailyTool } = useCreateDailyToolMutation({ onSuccess: refetch });

    const { selectedItem, manageModal, handleMove, handleDelete, handleDuplicate, openModal } = useDailyResource<DailyTool>({
        onMove: async (fromLineId, itemId, toLineId) => {
            updateDailyTool({ id: itemId, body: { activityId: toLineId, createTransfer: true }, queries: { deleteTransfers: true } });
        },
        onDelete: async (itemId) => {
            deleteDailyTool({ id: itemId, queries: { deleteTransfers: true } });
        },
        onDuplicate: async (tool) => {
            createDailyTool({ activityId: activity._id, toolId: tool.toolId._id, state: tool.state, isUsed: true, createTransfer });
        },
    });

    const countDuplicates = (toolId: string) => {
        const matches = activities.flatMap((a) => a.dailyTools).filter((t) => t.toolId._id === toolId);

        if (matches.length === 1) return 1;
        if (
            matches.length === 2 &&
            matches.some((t) => t.state === materialStateEnum.NEW) &&
            matches.some((t) => t.state === materialStateEnum.REMOVE)
        )
            return 1;

        return matches.length;
    };

    const showCheckbox = activity.stepMode === stepModeEnum.REPORT || activity.stepMode === stepModeEnum.VALIDATE;

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%', flex: 1, opacity: disabled ? 0.7 : 1, color: 'black' }}>
            <DroppableCell dropId={{ lineId: activity._id }} moveCell={handleMove} itemType={itemType} disabled={disabled}>
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addCircle"
                                onClick={() => openModal(null)}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addTool'))}
                            />
                        )}
                    </div>
                    <div className="gap-5px fx-c fx-fg-1">
                        {activity.dailyTools.map((tool, index) => {
                            const duplicateCount = countDuplicates(tool.toolId._id);

                            return (
                                <div key={`${activityIndex}-${index}`} className="fx-r fx-fg-1 gap-5px fx-items-fs">
                                    <DraggableCell
                                        dragIds={{ itemId: tool._id, lineId: activity._id }}
                                        index={index}
                                        itemType={itemType}
                                        onDelete={() => handleDelete(tool._id)}
                                        disabled={disabled}
                                        state={tool.state}
                                        warning={duplicateCount > 1}
                                        warningChar={duplicateCount.toString()}
                                        onDuplicate={() => handleDuplicate(tool)}
                                        onDoubleClick={() => openModal(tool)}
                                    >
                                        <div className="one-line-container">
                                            <div className="one-line" title={tool.toolId.designation}>
                                                {tool.toolId.designation}
                                            </div>
                                            {activity.stepMode === stepModeEnum.VALIDATE && (
                                                <div className="one-line" title={tool.toolId.codeProC}>
                                                    {tool.toolId.codeProC}
                                                </div>
                                            )}
                                        </div>
                                    </DraggableCell>

                                    {showCheckbox && (
                                        <div className="h-25px fx-r fx-items-c" title={capitalizeFirstLetter(t('isUsed'))}>
                                            <Checkbox
                                                checked={tool.isUsed}
                                                onChange={(_, checked) => {
                                                    updateDailyTool(
                                                        { id: tool._id, body: { isUsed: checked } },
                                                        {
                                                            onSuccess: () => {
                                                                refetch();
                                                            },
                                                        }
                                                    );
                                                }}
                                                size="small"
                                                color="success"
                                                sx={{ padding: 0 }}
                                                disabled={disabled || !activity.includedInExpenses}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </DroppableCell>

            {manageModal.value && (
                <ManageDailyToolModal
                    open={manageModal.value}
                    activityId={activity._id}
                    handleClose={() => manageModal.setFalse()}
                    refetch={refetch}
                    dailyTool={selectedItem}
                    createTransfer={createTransfer}
                    activities={activities}
                    tool={null}
                    stepMode={activity.stepMode}
                />
            )}
        </Box>
    );
};

export default ToolsCell;
